import type { StaffScheduleEntry } from "@bednbody/types";
import { Badge } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import { Checkbox } from "@bednbody/ui/components/checkbox";
import { entryFor, timeInManila, WEEKDAY_LABELS } from "../schedule-display";
import type { ScheduleStaff } from "../schedules.hooks";

interface WeeklyScheduleGridProps {
  staff: ScheduleStaff[];
  dates: string[];
  entries: StaffScheduleEntry[];
  editable: boolean;
  selectedStaffIds: Set<string>;
  onSelectStaff: (staffId: string, selected: boolean) => void;
  onEdit: (staff: ScheduleStaff, scheduleDate: string, entry?: StaffScheduleEntry) => void;
}

export function WeeklyScheduleGrid({
  staff,
  dates,
  entries,
  editable,
  selectedStaffIds,
  onSelectStaff,
  onEdit,
}: WeeklyScheduleGridProps) {
  if (staff.length === 0) {
    return (
      <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">
        No therapists or riders are assigned to this hub.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border bg-card">
      <table className="w-full min-w-[1040px] border-collapse text-sm">
        <thead>
          <tr className="border-b bg-muted/40">
            <th className="sticky left-0 z-10 min-w-56 bg-muted px-3 py-3 text-left font-medium">
              Staff
            </th>
            {dates.map((date, index) => (
              <th key={date} className="min-w-28 px-2 py-3 text-center font-medium">
                <span className="block">{WEEKDAY_LABELS[index]}</span>
                <span className="text-xs font-normal text-muted-foreground">{date.slice(5)}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(["therapist", "rider"] as const).map((role) => {
            const group = staff.filter((person) => person.role === role);
            if (group.length === 0) return null;
            return (
              <ScheduleRoleRows
                key={role}
                role={role}
                staff={group}
                dates={dates}
                entries={entries}
                editable={editable}
                selectedStaffIds={selectedStaffIds}
                onSelectStaff={onSelectStaff}
                onEdit={onEdit}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ScheduleRoleRows(props: WeeklyScheduleGridProps & { role: "therapist" | "rider" }) {
  const label = props.role === "therapist" ? "Massage Therapists" : "Transport Riders";
  return (
    <>
      <tr className="border-b bg-secondary/40">
        <th
          colSpan={8}
          className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide"
        >
          {label}
        </th>
      </tr>
      {props.staff.map((person) => (
        <tr key={person.id} className="border-b last:border-0">
          <th className="sticky left-0 z-10 bg-card px-3 py-3 text-left font-normal">
            <div className="flex items-start gap-2">
              {props.editable ? (
                <Checkbox
                  className="mt-1"
                  aria-label={`Select ${person.name} for bulk schedule changes`}
                  checked={props.selectedStaffIds.has(person.id)}
                  onCheckedChange={(checked) => props.onSelectStaff(person.id, checked === true)}
                />
              ) : null}
              <div>
                <span className="block font-medium text-foreground">{person.name}</span>
                <span className="text-xs text-muted-foreground">{person.hubName}</span>
                {!person.active ? (
                  <Badge variant="destructive" className="mt-1 block">
                    Inactive
                  </Badge>
                ) : null}
              </div>
            </div>
          </th>
          {props.dates.map((date) => {
            const entry = entryFor(props.entries, person.id, date);
            return (
              <td key={date} className="border-l p-1.5 align-top">
                <Button
                  type="button"
                  variant="ghost"
                  className="h-auto min-h-16 w-full flex-col rounded-lg px-2 py-2 text-center"
                  disabled={!props.editable}
                  onClick={() => props.onEdit(person, date, entry)}
                  aria-label={`${person.name}, ${date}: ${
                    entry?.kind === "rest_day"
                      ? "Rest Day"
                      : entry?.kind === "duty"
                        ? "Duty"
                        : "Not set"
                  }`}
                >
                  {entry?.kind === "duty" ? (
                    <>
                      <Badge>Duty</Badge>
                      <span className="text-xs text-muted-foreground">
                        {timeInManila(entry.dutyStart)}–{timeInManila(entry.dutyEnd)}
                      </span>
                    </>
                  ) : entry?.kind === "rest_day" ? (
                    <Badge variant="secondary">Rest Day</Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">Not set</span>
                  )}
                </Button>
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}
